import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { api, externalApi } from '../../../../services/authService';
import { selectCurrentToken } from '../../../../redux/authSlice';
import MessageTemplateService from '../../../../services/messageTemplateService';
import moment from 'moment-timezone';
import BasicInfoForm from '../../../../components/campaigns/BasicInfoForm';
import TestBlastCampaign from '../../../../components/campaigns/blast/TestBlastCampaign';
import CampaignHeader from '../../../../components/campaigns/CampaignHeader';
import CampaignTabs from '../../../../components/campaigns/CampaignTabs';
import BlastMessageConfig from '../../../../components/campaigns/blast/BlastMessageConfig';
import BlastScheduleConfig from '../../../../components/campaigns/blast/BlastScheduleConfig';
import CommunicationEndpointsProvider from '../../../../components/campaigns/CommunicationEndpointsProvider';
import OptOutConfig from '../../../../components/campaigns/OptOutConfig';
import RetryConfigurationPanel from '../../../../components/campaigns/RetryConfigurationPanel';
import { formatRetryConfigForBackend } from '../../../../utils/retryStrategyUtils';

// Get list of timezones from moment-timezone
const timezones = moment.tz.names();

const EditBlastCampaign = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const token = useSelector(selectCurrentToken);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [crmCampaigns, setCrmCampaigns] = useState([]);
  const [statusChoices, setStatusChoices] = useState([]);
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [communicationEndpoints, setCommunicationEndpoints] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    account: '',
    crm_campaign: '',
    start_date: '',
    end_date: '',
    active: true,
    is_ongoing: false,
    campaign_type: 'blast',
    channel: 'email',
    template: '',
    content: '',
    status: 'draft',
    auto_enroll_new_leads: false,
    auto_enroll_filters: [],
    // Retry configuration
    retry_strategy_id: null,
    max_retries: null,
    email_config: {
      content: '',
      subject: '',
      from_endpoint: '',
      from_name: '',
      reply_to: '',
      priority: 'normal',
      track_opens: true,
      track_clicks: true,
      attachments: []
    },
    sms_config: {
      content: '',
      from_endpoint: '',
      priority: 'normal',
      track_delivery: true,
      track_replies: true,
      media_urls: []
    },
    voice_config: {
      content: '',
      from_endpoint: '',
      voice: 'female',
      language: 'en-US',
      priority: 'normal',
      retry_attempts: 3,
      retry_delay: 300,
      record_call: true,
      call_timeout: 60,
      machine_detection: 'prefer_human'
    },
    chat_config: {
      content: '',
      from_endpoint: '',
      platform: 'whatsapp',
      priority: 'normal',
      track_delivery: true,
      track_read: true,
      media_urls: [],
      quick_replies: []
    },
    blast_schedule: {
      business_hours_only: true,
      send_time: '',
      timezone: 'UTC'
    },
    lead_filters: [],
    enable_opt_out: false,
    initial_opt_out_notice: '',
    opt_out_message: ''
  });

  const [testData, setTestData] = useState({
    email: '',
    phone: '',
    testStatus: null,
    testError: null
  });

  // Load campaign data on component mount
  useEffect(() => {
    const fetchCampaignData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [campaignResponse, accountsResponse, templatesResponse, statusResponse] = await Promise.all([
          api.get(`/acs/nurturing-campaigns/${campaignId}/`),
          api.get('/crm/accounts/'),
          MessageTemplateService.getTemplates({ channel: formData.channel }),
          api.get('/acs/nurturing-campaigns/status_choices/')
        ]);

        const campaign = campaignResponse.data;
        setFormData({
          ...campaign,
          email_config: {
            ...formData.email_config,
            ...campaign.email_config
          },
          sms_config: {
            ...formData.sms_config,
            ...campaign.sms_config
          },
          voice_config: {
            ...formData.voice_config,
            ...campaign.voice_config
          },
          chat_config: {
            ...formData.chat_config,
            ...campaign.chat_config
          },
          template: campaign.template || '',
          content: campaign.content || '',
          start_date: campaign.start_date ? new Date(campaign.start_date).toISOString().split('T')[0] : '',
          end_date: campaign.end_date ? new Date(campaign.end_date).toISOString().split('T')[0] : '',
          blast_schedule: {
            ...formData.blast_schedule,
            ...campaign.blast_schedule,
            send_time: campaign.blast_schedule?.send_time || '',
            timezone: campaign.blast_schedule?.timezone || 'UTC',
            business_hours_only: campaign.blast_schedule?.business_hours_only ?? true
          },
        });

        setAccounts(accountsResponse.data);
        setTemplates(templatesResponse);
        setStatusChoices(statusResponse.data);

        // Load CRM campaigns for the selected account
        if (campaign.account) {
          const crmCampaignsResponse = await api.get(`/crm/accounts/${campaign.account}/campaigns/`);
          setCrmCampaigns(crmCampaignsResponse.data);
        }

        setDataLoaded(true);
      } catch (err) {
        console.error('Error loading campaign data:', err);
        setError('Failed to load campaign data');
      } finally {
        setLoading(false);
      }
    };
    fetchCampaignData();
  }, [campaignId]);

  // Add effect to reload templates when channel changes
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const templatesResponse = await MessageTemplateService.getTemplates({ channel: formData.channel });
        setTemplates(templatesResponse);
      } catch (err) {
        console.error('Error loading templates:', err);
      }
    };
    loadTemplates();
  }, [formData.channel]);

  // Fetch CRM campaigns when account changes
  useEffect(() => {
    const fetchCrmCampaigns = async () => {
      if (formData.account) {
        try {
          const response = await api.get(`/crm/accounts/${formData.account}/campaigns/`);
          setCrmCampaigns(response.data);
        } catch (err) {
          setError('Failed to load CRM campaigns');
          console.error('Error loading CRM campaigns:', err);
        }
      } else {
        setCrmCampaigns([]);
      }
    };
    fetchCrmCampaigns();
  }, [formData.account]);

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    
    if (name.startsWith('email_config.')) {
      const configKey = name.split('.')[2];
      setFormData((prev) => ({
        ...prev,
        email_config: {
          ...prev.email_config,
          [configKey]: e.target.type === 'checkbox' ? checked : value,
        },
      }));
    } else if (name.startsWith('sms_config.')) {
      const configKey = name.split('.')[2];
      setFormData((prev) => ({
        ...prev,
        sms_config: {
          ...prev.sms_config,
          [configKey]: e.target.type === 'checkbox' ? checked : value,
        },
      }));
    } else if (name.startsWith('voice_config.')) {
      const configKey = name.split('.')[2];
      setFormData((prev) => ({
        ...prev,
        voice_config: {
          ...prev.voice_config,
          [configKey]: e.target.type === 'checkbox' ? checked : value,
        },
      }));
    } else if (name.startsWith('chat_config.')) {
      const configKey = name.split('.')[2];
      setFormData((prev) => ({
        ...prev,
        chat_config: {
          ...prev.chat_config,
          [configKey]: e.target.type === 'checkbox' ? checked : value,
        },
      }));
    } else if (name.startsWith('blast_schedule.')) {
      const scheduleKey = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        blast_schedule: {
          ...prev.blast_schedule,
          [scheduleKey]: e.target.type === 'checkbox' ? checked : value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: e.target.type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setSaveSuccess(false);

    try {
      // Create a clean payload with only necessary fields
      const cleanPayload = {
        name: formData.name,
        description: formData.description,
        account: formData.account,
        crm_campaign: formData.crm_campaign,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.is_ongoing ? null : (formData.end_date ? new Date(formData.end_date).toISOString() : null),
        active: formData.active,
        is_ongoing: formData.is_ongoing,
        campaign_type: 'blast',
        channel: formData.channel,
        template: formData.template || null,
        status: formData.status,
        auto_enroll_new_leads: formData.auto_enroll_new_leads,
        auto_enroll_filters: formData.auto_enroll_filters,
        lead_filters: formData.lead_filters,
        enable_opt_out: formData.enable_opt_out ?? false,
        initial_opt_out_notice: formData.initial_opt_out_notice || '',
        opt_out_message: formData.opt_out_message || '',
        // Retry configuration - ensure proper integer formatting
        ...formatRetryConfigForBackend(formData.retry_strategy_id, formData.max_retries)
      };

      // Add the channel config
      const channelConfig = { ...formData[`${formData.channel}_config`] };
      
      // Keep the ID if it exists (for updates)
      if (channelConfig.id) {
        channelConfig.id = channelConfig.id;
      }
      
      // Set the from_endpoint_id to the from_endpoint value
      if (channelConfig.from_endpoint) {
        channelConfig.from_endpoint_id = channelConfig.from_endpoint;
      }
      
      channelConfig.nurturing_campaign = campaignId;

      // Add the channel config
      cleanPayload[`${formData.channel}_config`] = channelConfig;

      // Add blast schedule config
      const scheduleConfig = { ...formData.blast_schedule };

      // Remove unnecessary fields from schedule config
      delete scheduleConfig.id;
      delete scheduleConfig.campaign;
      delete scheduleConfig.created_at;
      delete scheduleConfig.updated_at;

      // Send the time exactly as selected in the UI, let backend handle timezone conversion
      if (scheduleConfig.send_time) {
        scheduleConfig.send_time = scheduleConfig.send_time;
      }

      cleanPayload.blast_schedule = scheduleConfig;

      // Update the campaign
      const response = await api.put(`/acs/blast-campaigns/${campaignId}/`, cleanPayload);

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Campaign update error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to update campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    setLoading(true);
    setError(null);

    try {
      await api.post(`/acs/nurturing-campaigns/${campaignId}/update_status/`, {
        status: 'active'
      });
      setActivateDialogOpen(false);
      navigate('/crm/lead-nurturing');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to activate campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleTestCampaign = async () => {
    setTestData(prev => ({ ...prev, testStatus: 'loading', testError: null }));
    try {
      const message = formData.template ? 
        templates.find(t => t.id === formData.template)?.content : 
        formData[`${formData.channel}_config`].content;
      const fromEndpoint = formData[`${formData.channel}_config`].from_endpoint_value;

      if (!message || !fromEndpoint) {
        throw new Error('Message content and from endpoint are required');
      }

      const testPayload = {
        phone_number: testData.phone,
        message: message,
        twilio_number: fromEndpoint
      };
      
      const response = await externalApi.post('/communications/send-direct-sms/', testPayload);
      
      if (response.data.success) {
        setTestData(prev => ({ 
          ...prev, 
          testStatus: 'success', 
          testError: null 
        }));
      } else {
        throw new Error(response.data.detail || 'Failed to send test message');
      }
    } catch (err) {
      setTestData(prev => ({
        ...prev,
        testStatus: 'error',
        testError: err.response?.data?.detail || err.message || 'Failed to send test message'
      }));
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderMessageConfig = () => {
    return (
      <CommunicationEndpointsProvider
        accountId={formData.account}
        campaignId={formData.crm_campaign}
        channel={formData.channel}
        activeTab={activeTab}
      >
        {({ endpoints, loading: endpointsLoading, error: endpointsError }) => (
          <Grid container spacing={3}>
            {endpointsError && (
              <Grid item xs={12}>
                <Alert severity="error">{endpointsError}</Alert>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <BlastMessageConfig
                template={formData.template}
                content={formData[`${formData.channel}_config`].content}
                onTemplateChange={(value) => {
                  setFormData(prev => ({
                    ...prev,
                    template: value,
                    [`${prev.channel}_config`]: {
                      ...prev[`${prev.channel}_config`],
                      content: value ? '' : prev[`${prev.channel}_config`].content
                    }
                  }));
                }}
                onContentChange={(value) => {
                  setFormData(prev => ({
                    ...prev,
                    template: '',
                    [`${prev.channel}_config`]: {
                      ...prev[`${prev.channel}_config`],
                      content: value
                    }
                  }));
                }}
                templates={templates}
                channel={formData.channel}
                channelConfig={formData[`${formData.channel}_config`]}
                onChannelConfigChange={(config) => {
                  setFormData(prev => ({
                    ...prev,
                    [`${prev.channel}_config`]: config
                  }));
                }}
                communicationEndpoints={endpoints}
                onEndpointChange={(value) => {
                  setFormData(prev => ({
                    ...prev,
                    [`${prev.channel}_config`]: {
                      ...prev[`${prev.channel}_config`],
                      from_endpoint: value
                    }
                  }));
                }}
                onChannelChange={(value) => {
                  setFormData(prev => ({
                    ...prev,
                    channel: value,
                    template: '', // Reset template when channel changes
                    [`${value}_config`]: {
                      ...prev[`${value}_config`],
                      content: '' // Reset content when channel changes
                    }
                  }));
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <OptOutConfig
                config={{
                  enable_opt_out: formData.enable_opt_out,
                  initial_opt_out_notice: formData.initial_opt_out_notice,
                  opt_out_message: formData.opt_out_message
                }}
                onChange={(updatedConfig) => {
                  setFormData(prev => ({
                    ...prev,
                    ...updatedConfig
                  }));
                }}
              />
            </Grid>
          </Grid>
        )}
      </CommunicationEndpointsProvider>
    );
  };

  const renderScheduleSettings = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BlastScheduleConfig
          schedule={formData.blast_schedule}
          onScheduleChange={(schedule) => {
            setFormData(prev => ({
              ...prev,
              blast_schedule: {
                ...prev.blast_schedule,
                ...schedule
              }
            }));
          }}
          timezones={timezones}
        />
      </Grid>
      
      {/* Retry Configuration */}
      <Grid item xs={12}>
        <Paper sx={{ p: 2, mt: 2 }}>
          <RetryConfigurationPanel
            retryStrategy={formData.retry_strategy_id}
            maxRetries={formData.max_retries}
            onRetryStrategyChange={(strategyId) => setFormData(prev => ({ ...prev, retry_strategy_id: strategyId }))}
            onMaxRetriesChange={(maxRetries) => setFormData(prev => ({ ...prev, max_retries: maxRetries }))}
            description="Configure retry behavior for blast messages"
          />
        </Paper>
      </Grid>
    </Grid>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <CampaignHeader
          title="Edit Blast Campaign"
          loading={loading}
          onBack={() => navigate('/crm/lead-nurturing')}
          onSave={handleSubmit}
          onActivate={() => setActivateDialogOpen(true)}
          showActivateButton={true}
          status={formData.status}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Campaign updated successfully!
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress size={60} />
          </Box>
        ) : dataLoaded ? (
          <>
            <CampaignTabs
              activeTab={activeTab}
              onTabChange={handleTabChange}
              campaignType="blast"
              isEdit={true}
            />

            <Box sx={{ mt: 3 }}>
              {activeTab === 0 && (
                <BasicInfoForm
                  formData={formData}
                  onInputChange={handleInputChange}
                  accounts={accounts}
                  crmCampaigns={crmCampaigns}
                  campaignTypes={{ blast: 'Blast Campaign' }}
                />
              )}
              {activeTab === 1 && renderMessageConfig()}
              {activeTab === 2 && renderScheduleSettings()}
              {activeTab === 3 && (
                <CommunicationEndpointsProvider
                  accountId={formData.account}
                  campaignId={formData.crm_campaign}
                  channel={formData.channel}
                  activeTab={activeTab}
                >
                  {({ endpoints, loading: endpointsLoading, error: endpointsError }) => (
                    <TestBlastCampaign
                      formData={formData}
                      testData={testData}
                      onTestDataChange={setTestData}
                      onTestCampaign={handleTestCampaign}
                      templates={templates}
                      communicationEndpoints={endpoints}
                    />
                  )}
                </CommunicationEndpointsProvider>
              )}
            </Box>
          </>
        ) : null}
      </Paper>

      <Dialog
        open={activateDialogOpen}
        onClose={() => setActivateDialogOpen(false)}
      >
        <DialogTitle>Activate Campaign</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to activate this campaign? Once activated, the campaign will start sending messages according to the schedule.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleActivate} color="success" variant="contained">
            Activate
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EditBlastCampaign; 